import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

import { VpTabButtonGroup } from '@vp-ui/components';
import VpMediaInfoMenu from '@vp-ui/widgets/VpMediaTable/VpMediaInfoMenu';
import VpAttachmentInfoMenu from '@vp-ui/widgets/VpMediaTable/VpAttachmentInfoMenu';
import VpMediaInfoPreview from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoPreview';
import VpMediaInfoDetails from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoDetails';
import VpMediaInfoCaptions from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoCaptions';
import VpMediaInfoTranslation from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoTranslation';
import VpMediaInfoPublish from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoPublish';
import VpMediaInfoBranding from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoBranding';
import VpMediaInfoInsights from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoInsights';
import { useMediaTableContext } from '@src/hooks/contexts';
import { MEDIA_TABS, ATTACHMENT_TABS } from '@src/constants/media-tabs';

const boxStyles = {
  flex: 1,
  padding: '25px',
  overflowY: 'auto',
  minHeight: 0
};
const stackStyles = {
  gap: '25px'
};

const VpMediaInfo = ({ media, videos = null, campaignLink = null }) => {
  const isVideo = !!media?.mediaId;
  const mediaId = media?.mediaId || media?.attachmentId;
  const [selectedTab, setSelectedTab] = useState(0);
  const { pendingTab, setPendingTab } = useMediaTableContext();
  const lastMediaIdRef = useRef(mediaId);

  // Back to Preview only when the viewer moves from one media to another. This panel can mount before
  // the dialog has picked a media (or see the pick cleared and redone), and neither is a switch: both
  // must keep the tab a navigation asked for.
  useEffect(() => {
    if (!mediaId) return;
    if (lastMediaIdRef.current && lastMediaIdRef.current !== mediaId) setSelectedTab(0);
    lastMediaIdRef.current = mediaId;
  }, [mediaId]);

  useEffect(() => {
    if (pendingTab !== null) {
      setSelectedTab(pendingTab);
      setPendingTab(null);
    }
  }, [pendingTab, setPendingTab]);

  return (
    <Box sx={boxStyles}>
      <Stack sx={stackStyles}>
        <Stack direction="row" alignItems="center" gap={1} justifyContent="space-between">
          {isVideo ? (
            <VpTabButtonGroup
              first="Preview"
              second="Branding"
              third="Caption"
              fourth="Translate"
              fifth="Publish"
              sixth="Insights"
              selected={selectedTab}
              onSelect={setSelectedTab}
            />
          ) : (
            <VpTabButtonGroup
              first="Preview"
              second="Details"
              selected={selectedTab !== ATTACHMENT_TABS.PREVIEW}
              onSelect={(val) => setSelectedTab(val ? ATTACHMENT_TABS.DETAILS : ATTACHMENT_TABS.PREVIEW)}
            />
          )}
          <Stack direction="row" gap="5px">
            {isVideo ? <VpMediaInfoMenu media={media} /> : <VpAttachmentInfoMenu media={media} />}
          </Stack>
        </Stack>
        {selectedTab === MEDIA_TABS.PREVIEW && <VpMediaInfoPreview key={mediaId} media={media} />}
        {selectedTab === MEDIA_TABS.BRANDING && isVideo && <VpMediaInfoBranding key={mediaId} media={media} />}
        {selectedTab === MEDIA_TABS.CAPTION && isVideo && <VpMediaInfoCaptions key={mediaId} media={media} />}
        {selectedTab === ATTACHMENT_TABS.DETAILS && !isVideo && <VpMediaInfoDetails key={mediaId} media={media} />}
        {selectedTab === MEDIA_TABS.TRANSLATE && isVideo && <VpMediaInfoTranslation key={mediaId} media={media} />}
        {selectedTab === MEDIA_TABS.PUBLISH && isVideo && <VpMediaInfoPublish key={mediaId} media={media} />}
        {/* Insights are the response's, not the selected video's: its key is the response's first video. */}
        {selectedTab === MEDIA_TABS.INSIGHTS && isVideo && (
          <VpMediaInfoInsights key={videos?.[0]?.mediaId || mediaId} mediaId={mediaId} videos={videos} campaignLink={campaignLink} />
        )}
      </Stack>
    </Box>
  );
};

VpMediaInfo.propTypes = {
  media: PropTypes.shape({
    mediaId: PropTypes.string,
    attachmentId: PropTypes.string
  }),
  videos: PropTypes.arrayOf(PropTypes.shape({ mediaId: PropTypes.string, question: PropTypes.string })),
  campaignLink: PropTypes.string
};
export default VpMediaInfo;



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfo.jsx